/**
 * CX-G08 normative z-score helpers + CX-G09 vsBaseline delta math.
 */

export const NORMATIVE_ZMAP_ALGORITHM_ID = "normative-zmap-v1.0.0";
export const NORMATIVE_ZMAP_MODEL_CARD_URI =
  "qms://model-card/normative-zmap-v1";
export const VSBASELINE_ALGORITHM_ID = "qeeg-vs-baseline-v1.0.0";

export type NormativeAgeBand = {
  bandId: string;
  label: string;
  ageMinYears: number;
  ageMaxYears: number;
};

export type NormativeChannelStat = {
  channel: string;
  band: string;
  mean: number;
  std: number;
};

export const DEFAULT_NORMATIVE_AGE_BANDS: NormativeAgeBand[] = [
  { bandId: "adult_18_40", label: "Adult 18–40", ageMinYears: 18, ageMaxYears: 40 },
  { bandId: "adult_41_65", label: "Adult 41–65", ageMinYears: 41, ageMaxYears: 65 },
  { bandId: "older_66_plus", label: "Older 66+", ageMinYears: 66, ageMaxYears: 120 },
];

/** Compact assistive normative table (placeholder means; provenance required in UI). */
export const DEFAULT_NORMATIVE_BANDPOWER_STATS: NormativeChannelStat[] = [
  { channel: "*", band: "alpha", mean: 12, std: 4 },
  { channel: "*", band: "theta", mean: 8, std: 3 },
  { channel: "*", band: "delta", mean: 15, std: 5 },
  { channel: "*", band: "beta", mean: 6, std: 2.5 },
  { channel: "*", band: "gamma", mean: 3, std: 1.5 },
];

export function resolveNormativeAgeBand(
  ageYears: number | null | undefined,
  bands: NormativeAgeBand[] = DEFAULT_NORMATIVE_AGE_BANDS,
): NormativeAgeBand | null {
  if (ageYears == null || !Number.isFinite(ageYears)) return bands[0] || null;
  return (
    bands.find((b) => ageYears >= b.ageMinYears && ageYears <= b.ageMaxYears) ||
    bands[bands.length - 1] ||
    null
  );
}

export function computeZScore(
  value: number,
  mean: number,
  std: number,
): number {
  if (!(std > 0) || !Number.isFinite(value) || !Number.isFinite(mean)) return 0;
  return (value - mean) / std;
}

export function lookupNormativeStat(
  channel: string,
  band: string,
  stats: NormativeChannelStat[] = DEFAULT_NORMATIVE_BANDPOWER_STATS,
): NormativeChannelStat {
  const exact = stats.find(
    (s) =>
      s.channel.toLowerCase() === channel.toLowerCase() &&
      s.band.toLowerCase() === band.toLowerCase(),
  );
  if (exact) return exact;
  const wildcard = stats.find(
    (s) =>
      s.channel === "*" && s.band.toLowerCase() === band.toLowerCase(),
  );
  return wildcard || { channel, band, mean: 0, std: 1 };
}

export function buildNormativeZMapPoints(
  channelValues: Array<{ channel: string; value: number }>,
  band: string,
  stats: NormativeChannelStat[] = DEFAULT_NORMATIVE_BANDPOWER_STATS,
): Array<{ channel: string; value: number; z: number }> {
  return channelValues.map((row) => {
    const stat = lookupNormativeStat(row.channel, band, stats);
    return {
      channel: row.channel,
      value: row.value,
      z: computeZScore(row.value, stat.mean, stat.std),
    };
  });
}

export type TrendSample = { timeSeconds: number; value: number };

/**
 * Convert a series to change-from-baseline (fractional delta vs baseline mean).
 */
export function computeVsBaselineSeries(
  samples: TrendSample[],
  baselineFromSeconds: number,
  baselineToSeconds: number,
): {
  samples: TrendSample[];
  baselineMean: number;
  baselineSampleCount: number;
} {
  const from = Math.min(baselineFromSeconds, baselineToSeconds);
  const to = Math.max(baselineFromSeconds, baselineToSeconds);
  const baseline = samples.filter(
    (s) => s.timeSeconds >= from && s.timeSeconds <= to,
  );
  const baselineMean =
    baseline.length > 0
      ? baseline.reduce((sum, s) => sum + s.value, 0) / baseline.length
      : 0;
  const denom = Math.abs(baselineMean) > 1e-9 ? baselineMean : 1;
  return {
    baselineMean,
    baselineSampleCount: baseline.length,
    samples: samples.map((s) => ({
      timeSeconds: s.timeSeconds,
      value: (s.value - baselineMean) / denom,
    })),
  };
}
